/**
 * Product_Switcher model (pure, no React, no network).
 *
 * Each Feature maps to a lucide icon and an accent colour. A product is shown
 * when the tenant is entitled to its `TenantFeature`. Title Search has no
 * entitlement key in the backend yet (`platform_admin_service/models.py` only
 * defines DD and BA), so it is ungated (`feature: null`) until one exists.
 *
 * Requirements: 2.1, 2.2
 */

import type { LucideIcon } from 'lucide-react';
import { ClipboardCheck, House, UserCheck } from 'lucide-react';
import { TenantFeature } from '../../types/admin';

export type ProductId = 'DD' | 'BA' | 'TR';

export interface Product {
  id: ProductId;
  name: string;
  href: string;
  icon: LucideIcon;
  /** Feature accent colour (ui-design-system.md). */
  accent: string;
  /** Entitlement key, or null when the Feature is not gated by a tenant flag. */
  feature: TenantFeature | null;
}

/** Display order of the switcher. */
export const PRODUCTS: readonly Product[] = [
  {
    id: 'DD',
    name: 'Due Diligence',
    href: '/due-diligence',
    icon: ClipboardCheck,
    accent: '#834DFB',
    feature: TenantFeature.DUE_DILIGENCE_ANALYSIS,
  },
  {
    id: 'BA',
    name: 'Borrower Analysis',
    href: '/borrower-analysis',
    icon: UserCheck,
    accent: '#A34DF5',
    feature: TenantFeature.BORROWER_ANALYSIS,
  },
  {
    id: 'TR',
    name: 'Title Search',
    href: '/title-search',
    icon: House,
    accent: '#5A21C9',
    feature: null,
  },
];

/**
 * Products the tenant may use, in display order.
 * `allAccess` is for Platform Admins, who work across tenants.
 */
export function entitledProducts(
  enabledFeatures: readonly string[],
  allAccess = false
): Product[] {
  return PRODUCTS.filter(
    (p) => allAccess || p.feature === null || enabledFeatures.includes(p.feature)
  );
}

/** The product whose home or Workspace the path belongs to, if any. */
export function productForPath(pathname: string): Product | undefined {
  return PRODUCTS.find((p) => pathname === p.href || pathname.startsWith(`${p.href}/`));
}
